import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemarksThread from "./components/RemarksThread";
import { v4 as uuidv4 } from "uuid";
import config from "../../../../aws-exports";
import dayjs from "dayjs";
import {
  fetchData,
  postQuery,
  uploadFileToS3Path,
} from "../../../../services/BackendServices";
import AlertModal from "../../../Requirement/components/common/AlerModel";

const tabIcons = [
  "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13h-8 M16 17h-8 M10 9h1",
  "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75 M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6v6l4 2",
  "M9 12l2 2 4-4 M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6",
  "M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z M2 12h20 M12 2a10 10 0 0 0 0 20",
  "M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
];

const SectionHeader = ({ children }) => (
  <Box sx={{ mb: 3, pb: 2, borderBottom: "1px solid #e2e8f0" }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
      {children}
    </Typography>
  </Box>
);

const tabLabels = [
  "Legal Identity",
  "Ownership",
  "Trade History",
  "Compliance",
  "Operational",
  "Banking",
  "Remarks",
];

const KYBVerification = () => {
  const navigate = useNavigate();
  const currentDate = dayjs().format("YYYY-MM-DD");
  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  const bucketfolder = "public/media/amtpl/kybdoc";
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${bucketfolder}/`;

  const [formData, setFormData] = useState({
    legalName: "",
    hasTradingName: false,
    tradingName: "",
    businessType: "",
    industrySector: "",
    regNumber: "",
    incorporationDate: "",
    country: "",
    taxId: "",
    registeredAddress: "",
    isOperationalDifferent: false,
    operationalAddress: "",
    websiteEmail: "",
    contactNumber: "",
    annualTurnover: "",
    paidUpCapital: "",
    bankName: "",
    accountNumber: "",
    ifscSwift: "",
    sourceOfFunds: "",
    certificateOfIncorporation: null,
    taxIdProof: null,
    lastAuditedFinancials: null,
    Status: "",
    verifyConfirm: false,

    promoterName: "",
    ownershipDisclosed: "yes",
    shareholding: "",
    mgmtExperience: "",
    directorNames: "",
    tradingYears: "",
    tradeVolume: "",
    eximExperience: "yes",
    commodities: "",
    countriesTraded: "",
    amlPolicy: "yes",
    kycSubmitted: "yes",
    sanctionsCheck: true,
    litigation: "no",
    websiteUrl: "",
    emailDomain: "",
    websiteAge: "",
    phoneNumber: "",
    accountType: "",
    bankYears: "",
    lcUsage: "no",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeUser, setActiveUser] = useState("");
  const [activeUserName, setActiveUserName] = useState("");
  const [countrymaster, setCountryMaster] = useState([]);
  const [businessTypesMaster, setBusinessTypesMaster] = useState([]);
  const [industrySectorsMaster, setIndustrySectorsMaster] = useState([]);
  const [sourcesOfFundsMaster, setSourcesOfFundsMaster] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userKybID, setUserKybID] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const isRemarksTabAccessible = !isUpdateMode;

  const totalTabs = 6;

  const readOnly =
    formData.Status === "Approved" ||
    (isUpdateMode &&
      formData.Status &&
      formData.Status !== "Approved" &&
      !editMode);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const groupByType = (items) =>
    items.reduce((acc, cur) => {
      const type = cur.Type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(cur);
      return acc;
    }, {});

  const loadAllDropdowns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 10000,
      });
      const items = response.items ?? [];
      const groups = groupByType(items);
      setCountryMaster(groups.Country ?? []);
      setBusinessTypesMaster(groups.businessTypes ?? []);
      setIndustrySectorsMaster(groups.industrySectors ?? []);
      setSourcesOfFundsMaster(groups.sourcesOfFunds ?? []);
    } catch (err) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Could not load dropdown lists. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const checkDuplicateBankAccount = useCallback(
    async (accountNumber, currentUserKybID) => {
      if (!accountNumber) return false;
      try {
        const response = await fetchData({
          target: "listUserKYB",
          userOrder: "UserKybID",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { BankAccountNumber: accountNumber },
        });
        const matchingRecords = response.items.filter(
          (item) => !currentUserKybID || item.UserKybID !== currentUserKybID
        );
        return matchingRecords.length > 0;
      } catch (error) {
        setModal({
          show: true,
          type: "error",
          title: "Error!",
          message: "Failed to verify bank account number. Please try again.",
          autoClose: false,
        });
        return false;
      }
    },
    []
  );

  const validateField = async (name, value) => {
    let error = "";
    switch (name) {
      case "legalName":
      case "regNumber":
      case "taxId":
      case "registeredAddress":
      case "websiteEmail":
      case "contactNumber":
      case "bankName":
      case "accountNumber":
      case "ifscSwift":
      case "businessType":
      case "industrySector":
        if (!value?.trim()) error = "This field is required";
        break;
      case "country":
        if (!value) error = "Please select an option";
        break;
      case "verifyConfirm":
        if (!value) error = "This field is required";
        break;
      case "tradingName":
        if (formData.hasTradingName && !value?.trim())
          error = "This field is required";
        break;
      case "operationalAddress":
        if (formData.isOperationalDifferent && !value?.trim())
          error = "This field is required";
        break;
      case "incorporationDate":
        if (!value) error = "This field is required";
        else if (new Date(value) > new Date())
          error = "Date cannot be in the future";
        break;
      default:
        break;
    }

    if (name === "accountNumber" && value?.trim() && !error) {
      const isDuplicate = await checkDuplicateBankAccount(
        value.trim(),
        userKybID
      );
      if (isDuplicate)
        error =
          "This bank account number is already registered with another business";
    }
    return error;
  };

  const validateAllFields = async () => {
    const newErrors = {};
    const requiredFields = [
      "legalName",
      "regNumber",
      "taxId",
      "registeredAddress",
      "websiteEmail",
      "contactNumber",
      "bankName",
      "accountNumber",
      "ifscSwift",
      "businessType",
      "industrySector",
      "country",
      "incorporationDate",
      "verifyConfirm",
    ];

    for (const key of requiredFields) {
      const error = await validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    }

    if (formData.hasTradingName) {
      const error = await validateField("tradingName", formData.tradingName);
      if (error) newErrors.tradingName = error;
    }

    if (formData.isOperationalDifferent) {
      const error = await validateField(
        "operationalAddress",
        formData.operationalAddress
      );
      if (error) newErrors.operationalAddress = error;
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = async (e) => {
    if (readOnly) return;
    const { name, value, type, checked, files } = e.target;
    let newValue =
      type === "checkbox"
        ? checked
        : type === "file"
        ? files
          ? files[0]
          : null
        : value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      if (name === "legalName" && !prev.hasTradingName)
        updated.tradingName = newValue;
      if (name === "registeredAddress" && !prev.isOperationalDifferent)
        updated.operationalAddress = newValue;
      return updated;
    });

    if (touched[name]) {
      const error = await validateField(name, newValue);
      setErrors((prev) => ({ ...prev, [name]: error || undefined }));
    }
  };

  const handleBlur = async (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = await validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error || undefined }));
  };

  const handleFileRemove = (name) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [name]: null }));
  };

  const fetchExistingKYB = useCallback(async () => {
    if (!activeUser) return;
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listUserKYB",
        userOrder: "UserKybID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: activeUser },
      });

      if (response.items && response.items.length > 0) {
        const kybData = response.items[0];
        setIsUpdateMode(true);
        setUserKybID(kybData.UserKybID);
        setFormData({
          legalName: kybData.LegalBusinessName || "",
          hasTradingName:
            !!kybData.TradingName &&
            kybData.TradingName !== kybData.LegalBusinessName,
          tradingName: kybData.TradingName || "",
          businessType: kybData.BusinessType || "",
          industrySector: kybData.IndustrySector || "",
          regNumber: kybData.CompanyRegistrationNumber || "",
          incorporationDate: kybData.DateOfIncorporation
            ? dayjs(kybData.DateOfIncorporation).format("YYYY-MM-DD")
            : "",
          country: kybData.CountryOfIncorporation || "",
          taxId: kybData.TaxIdentificationNumber || "",
          registeredAddress: kybData.RegisteredAddress || "",
          isOperationalDifferent:
            !!kybData.OperationalAddress &&
            kybData.OperationalAddress !== kybData.RegisteredAddress,
          operationalAddress: kybData.OperationalAddress || "",
          websiteEmail: kybData.Website || "",
          contactNumber: kybData.ContactNumber || "",
          annualTurnover: kybData.AnnualTurnover?.toString() || "",
          paidUpCapital: kybData.PaidUpCapital?.toString() || "",
          bankName: kybData.BankName || "",
          accountNumber: kybData.BankAccountNumber || "",
          ifscSwift: kybData.IFSC_SwiftCode || "",
          sourceOfFunds: kybData.SourceOfFunds || "",
          certificateOfIncorporation: kybData.certificateOfIncorporation
            ? { name: kybData.certificateOfIncorporation }
            : null,
          taxIdProof: kybData.taxIdProof ? { name: kybData.taxIdProof } : null,
          lastAuditedFinancials: kybData.lastAuditedFinancials
            ? { name: kybData.lastAuditedFinancials }
            : null,
          Status: kybData.Status || "",
          verifyConfirm: true,
          promoterName: kybData.PromoterName || "",
          ownershipDisclosed: kybData.OwnershipDisclosed || "",
          shareholding: kybData.Shareholding || "",
          mgmtExperience: kybData.MgmtExperience || "",
          directorNames: kybData.DirectorNames || "",
          tradingYears: kybData.TradingYears || "",
          tradeVolume: kybData.TradeVolume || "",
          eximExperience: kybData.EximExperience || "",
          commodities: kybData.Commodities || "",
          countriesTraded: kybData.CountriesTraded || "",
          amlPolicy: kybData.AmlPolicy || "",
          kycSubmitted: kybData.KycSubmitted || "",
          sanctionsCheck: kybData.SanctionsCheck || "",
          litigation: kybData.Litigation || "",
          websiteUrl: kybData.WebsiteUrl || "",
          // emailDomain: kybData.Status || "",
          websiteAge: kybData.WebsiteAge || "",
          // phoneNumber: kybData.Status || "",
          accountType: kybData.AccountType || "",
          bankYears: kybData.BankYears || "",
          lcUsage: kybData.LcUsage || "",
        });
        setEditMode(false);
      }
    } catch (error) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed to load existing KYB data.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  }, [activeUser]);

  const handleSubmit = async () => {
    if (readOnly) return;
    setLoading(true);
    // **SIMPLE: Just await the entire validation**
    const validationErrors = await validateAllFields();

    setLoading(false);
    if (Object.keys(validationErrors).length > 0) {
      // Collect list of fields with errors
      const invalidFields = Object.keys(validationErrors)
        .map((key) => {
          const fieldLabels = {
            legalName: "Legal Business Name",
            tradingName: "Trading Name",
            businessType: "Business Type",
            industrySector: "Industry Sector",
            regNumber: "Registration Number",
            incorporationDate: "Date of Incorporation",
            country: "Country of Incorporation",
            taxId: "Tax Identification Number",
            registeredAddress: "Registered Address",
            operationalAddress: "Operational Address",
            websiteEmail: "Website / Official Email",
            contactNumber: "Contact Number",
            bankName: "Primary Operating Bank Name",
            accountNumber: "Bank Account Number",
            ifscSwift: "IFSC / SWIFT Code",
            verifyConfirm: "Verification Confirmation",
            // Add more mappings if needed for other fields
          };
          return fieldLabels[key] || key;
        })
        .join(", ");

      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: `Please correct the following fields: ${invalidFields}.`,
        autoClose: false,
      });
      return;
    }
    setLoading(true);
    try {
      let certificateRef = "";
      let taxProofRef = "";
      let financialsRef = "";

      if (formData.certificateOfIncorporation instanceof File) {
        const result = await uploadFileToS3Path(
          formData.certificateOfIncorporation,
          bucketfolder
        );
        certificateRef = result.fileName;
      }
      if (formData.taxIdProof instanceof File) {
        const result = await uploadFileToS3Path(
          formData.taxIdProof,
          bucketfolder
        );
        taxProofRef = result.fileName;
      }
      if (formData.lastAuditedFinancials instanceof File) {
        const result = await uploadFileToS3Path(
          formData.lastAuditedFinancials,
          bucketfolder
        );
        financialsRef = result.fileName;
      }

      const payload = {
        EmailID: activeUser,
        ContactNumber: formData.contactNumber,
        LegalBusinessName: formData.legalName,
        TradingName: formData.tradingName,
        BusinessType: formData.businessType,
        IndustrySector: formData.industrySector,
        CompanyRegistrationNumber: formData.regNumber,
        DateOfIncorporation: formData.incorporationDate,
        RegisteredAddress: formData.registeredAddress,
        OperationalAddress: formData.operationalAddress,
        CountryOfIncorporation: formData.country,
        TaxIdentificationNumber: formData.taxId,
        Website: formData.websiteEmail,
        AnnualTurnover: parseFloat(formData.annualTurnover) || 0,
        PaidUpCapital: parseFloat(formData.paidUpCapital) || 0,
        BankName: formData.bankName,
        BankAccountNumber: formData.accountNumber,
        IFSC_SwiftCode: formData.ifscSwift,
        SourceOfFunds: formData.sourceOfFunds,
        certificateOfIncorporation:
          certificateRef || formData.certificateOfIncorporation?.name || "",
        taxIdProof: taxProofRef || formData.taxIdProof?.name || "",
        lastAuditedFinancials:
          financialsRef || formData.lastAuditedFinancials?.name || "",
        Status: "Pending",
        DateOfSubmission: currentDate,
        PromoterName: formData.promoterName,
        OwnershipDisclosed: formData.ownershipDisclosed,
        Shareholding: formData.shareholding,
        MgmtExperience: formData.mgmtExperience,
        DirectorNames: formData.directorNames,
        TradingYears: formData.tradingYears,
        TradeVolume: formData.tradeVolume,
        EximExperience: formData.eximExperience,
        Commodities: formData.commodities,
        CountriesTraded: formData.countriesTraded,
        AmlPolicy: formData.amlPolicy,
        KycSubmitted: formData.kycSubmitted,
        SanctionsCheck: formData.sanctionsCheck,
        Litigation: formData.litigation,
        WebsiteUrl: formData.websiteUrl,
        WebsiteAge: formData.websiteAge,
        AccountType: formData.accountType,
        BankYears: formData.bankYears,
        LcUsage: formData.lcUsage,
      };

      if (isUpdateMode) {
        await postQuery({
          target: "updateUserKYB",
          userKYBInput: { ...payload, UserKybID: userKybID },
        });
      } else {
        const newId = uuidv4();
        await postQuery({
          target: "createUserKYB",
          userKYBInput: { ...payload, UserKybID: newId },
        });
        setUserKybID(newId);
        setIsUpdateMode(true);
      }

      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "KYB submitted successfully.",
        nextAction: () => window.location.reload(),
      });
    } catch (error) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to submit KYB.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFilePreview = (file, name) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "white",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 1,
        mt: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file instanceof File ? (
          file.name
        ) : (
          <a
            href={`${fileUrl}${file.name}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {file.name}
          </a>
        )}
      </Typography>
      <IconButton
        size="small"
        onClick={() => handleFileRemove(name)}
        disabled={readOnly}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const getStatusBadge = (status) => {
    const styles = {
      Pending: { bg: "#fef3c7", color: "#b45309" },
      Approved: { bg: "#d1fae5", color: "#065f46" },
      Verified: { bg: "#d1fae5", color: "#065f46" },
      default: { bg: "#f3f4f6", color: "#374151" },
    };
    const { bg, color } = styles[status] || styles.default;
    return (
      <Box
        sx={{
          backgroundColor: bg,
          color,
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "inline-block",
        }}
      >
        {status === "Pending" ? "In Review" : status}
      </Box>
    );
  };

  const goNext = () => activeTab < totalTabs && setActiveTab(activeTab + 1);
  const goPrev = () => activeTab > 0 && setActiveTab(activeTab - 1);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decoded = jwtDecode(token);
        if (!decoded["cognito:groups"]?.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
          setActiveUserName(user.attributes.given_name);
        }
      } catch (err) {
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
  }, [navigate]);

  useEffect(() => {
    loadAllDropdowns();
  }, [loadAllDropdowns]);

  useEffect(() => {
    if (activeUser) fetchExistingKYB();
  }, [activeUser, fetchExistingKYB]);

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="container kyb-verification mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div
        className="card p-4"
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              KYB Verification
            </Typography>
            <Typography sx={{ color: "#666", mt: 1 }}>
              Complete the following steps to verify your business.
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            {isUpdateMode && formData.Status && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Current Status: {getStatusBadge(formData.Status)}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {isUpdateMode &&
                formData.Status &&
                formData.Status !== "Approved" && (
                  <>
                    {!editMode ? (
                      <button
                        className="submit-btn-more"
                        onClick={() => setEditMode(true)}
                      >
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          className="submit-btn-more"
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </button>
                        <button
                          className="submit-btn-more"
                          color="error"
                          onClick={() => {
                            setEditMode(false);
                            fetchExistingKYB();
                          }}
                          disabled={loading}
                          style={{ backgroundColor: "#dc3545", color: "white" }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </>
                )}
              {!isUpdateMode && (
                <button
                  className="submit-btn-more"
                  onClick={handleSubmit}
                  disabled={loading || readOnly}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Tabs Header - Individual Tabs for Better Control */}
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            borderBottom: "2px solid #e2e8f0",
            mb: 4,
          }}
        >
          {/* Tab 0: Legal Identity */}
          <Box
            onClick={() => !readOnly && setActiveTab(0)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 0 ? "4px solid #ea580c" : "none",
              color: activeTab === 0 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 0 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[0]} />
            </svg>
            <Typography variant="caption">Legal Identity</Typography>
          </Box>

          {/* Tab 1: Ownership */}
          <Box
            onClick={() => !readOnly && setActiveTab(1)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 1 ? "4px solid #ea580c" : "none",
              color: activeTab === 1 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 1 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[1]} />
            </svg>
            <Typography variant="caption">Ownership</Typography>
          </Box>

          {/* Tab 2: Trade History */}
          <Box
            onClick={() => !readOnly && setActiveTab(2)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 2 ? "4px solid #ea580c" : "none",
              color: activeTab === 2 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 2 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[2]} />
            </svg>
            <Typography variant="caption">Trade History</Typography>
          </Box>

          {/* Tab 3: Compliance */}
          <Box
            onClick={() => !readOnly && setActiveTab(3)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 3 ? "4px solid #ea580c" : "none",
              color: activeTab === 3 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 3 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[3]} />
            </svg>
            <Typography variant="caption">Compliance</Typography>
          </Box>

          {/* Tab 4: Operational */}
          <Box
            onClick={() => !readOnly && setActiveTab(4)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 4 ? "4px solid #ea580c" : "none",
              color: activeTab === 4 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 4 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[4]} />
            </svg>
            <Typography variant="caption">Operational</Typography>
          </Box>

          {/* Tab 5: Banking */}
          <Box
            onClick={() => !readOnly && setActiveTab(5)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: !readOnly ? "pointer" : "default",
              borderBottom: activeTab === 5 ? "4px solid #ea580c" : "none",
              color: activeTab === 5 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 5 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[5]} />
            </svg>
            <Typography variant="caption">Banking</Typography>
          </Box>

          {/* Tab 6: Remarks */}
          <Box
            onClick={() => setActiveTab(6)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: "12px 20px",
              cursor: "pointer",
              borderBottom: activeTab === 6 ? "4px solid #ea580c" : "none",
              color: activeTab === 6 ? "#ea580c" : "#64748b",
              fontWeight: activeTab === 6 ? 700 : 500,
              minWidth: "140px",
              textAlign: "center",
              opacity: !isUpdateMode || readOnly ? 0.6 : 1,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d={tabIcons[6]} />
            </svg>
            <Typography variant="caption">Remarks</Typography>
          </Box>
        </Box>
        {/* Tab Content */}
        <Box>
          {/* Tab 0: Legal Identity */}
          {activeTab === 0 && (
            <Box>
              <SectionHeader variant="h6" gutterBottom>
                Legal & Regulatory Identity
              </SectionHeader>
              <Box className="tab-content">
                <div>
                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.legalName}>
                        <Typography variant="body2">
                          Legal Business Name *
                        </Typography>
                        <TextField
                          name="legalName"
                          value={formData.legalName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.legalName}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                            marginTop: "2px",
                          }}
                          helperText={errors.legalName}
                        />
                      </FormControl>
                    </div>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.tradingName}>
                        <Typography
                          variant="body2"
                          sx={{ margin: 0, padding: 0 }}
                        >
                          <FormControlLabel
                            sx={{ margin: 0, padding: 0 }}
                            control={
                              <Checkbox
                                name="hasTradingName"
                                checked={formData.hasTradingName}
                                onChange={(e) => {
                                  if (readOnly) return; // Prevent changes in read-only mode
                                  const checked = e.target.checked;
                                  setFormData((prev) => ({
                                    ...prev,
                                    hasTradingName: checked,
                                    tradingName: checked ? "" : prev.legalName,
                                  }));
                                  if (!checked) {
                                    setErrors((prev) => ({
                                      ...prev,
                                      tradingName: undefined,
                                    }));
                                  }
                                }}
                                disabled={readOnly} // Disable in read-only mode
                                sx={{
                                  margin: 0,
                                  padding: 0,
                                  transform: "scale(0.8)",
                                  color: "orange",
                                  "&.Mui-checked": { color: "orange" },
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 165, 0, 0.1)",
                                  },
                                  "& .MuiSvgIcon-root": { fontSize: 18 },
                                }}
                              />
                            }
                            label="Trading Name (if different)"
                          />
                        </Typography>
                        <TextField
                          name="tradingName"
                          value={formData.tradingName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!formData.hasTradingName || readOnly} // Disable if no trading name or read-only
                          error={!!errors.tradingName}
                          variant="outlined"
                          sx={{
                            backgroundColor:
                              !formData.hasTradingName || readOnly
                                ? "#f5f5f5"
                                : "white",
                          }}
                          helperText={errors.tradingName}
                        />
                      </FormControl>
                    </div>
                  </div>

                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.businessType}>
                        <Typography variant="body2">Business Type *</Typography>
                        <Select
                          name="businessType"
                          value={formData.businessType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.businessType}
                          disabled={readOnly} // Disable in read-only mode
                        >
                          <MenuItem value="" disabled>
                            Select Business Type
                          </MenuItem>
                          {/* {businessTypes.map((option) => (
                                     <MenuItem key={option.value} value={option.value}>
                                       {option.label}
                                     </MenuItem>
                                   ))} */}
                          {businessTypesMaster.map((p) => (
                            <MenuItem key={p.ID} value={p.Value}>
                              {p.Value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.businessType && (
                          <Typography variant="caption" color="error">
                            {errors.businessType}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.industrySector}>
                        <Typography variant="body2">
                          Industry Sector *
                        </Typography>
                        <Select
                          name="industrySector"
                          value={formData.industrySector}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.industrySector}
                          disabled={readOnly} // Disable in read-only mode
                        >
                          <MenuItem value="" disabled>
                            Select Industry Sector
                          </MenuItem>
                          {/* {industrySectors.map((option) => (
                                     <MenuItem key={option.value} value={option.value}>
                                       {option.label}
                                     </MenuItem>
                                   ))} */}
                          {industrySectorsMaster.map((p) => (
                            <MenuItem key={p.ID} value={p.Value}>
                              {p.Value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.industrySector && (
                          <Typography variant="caption" color="error">
                            {errors.industrySector}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                  </div>
                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.regNumber}>
                        <Typography variant="body2">
                          Registration Number (CIN / BRN) *
                        </Typography>
                        <TextField
                          name="regNumber"
                          value={formData.regNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.regNumber}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                          }}
                          helperText={errors.regNumber}
                        />
                      </FormControl>
                    </div>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.incorporationDate}>
                        <Typography variant="body2">
                          Date of Incorporation *
                        </Typography>
                        <TextField
                          name="incorporationDate"
                          type="date"
                          value={formData.incorporationDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.incorporationDate}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                          }}
                          helperText={errors.incorporationDate}
                          InputProps={{ shrink: true }}
                          inputProps={{ max: today }}
                        />
                      </FormControl>
                    </div>
                  </div>
                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.country}>
                        <Typography variant="body2">
                          Country of Incorporation *
                        </Typography>
                        <Select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.country}
                          disabled={readOnly} // Disable in read-only mode
                        >
                          <MenuItem value="" disabled>
                            Select Country
                          </MenuItem>
                          {countrymaster.map((p) => (
                            <MenuItem key={p.ID} value={p.Value}>
                              {p.Value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <Typography variant="caption" color="error">
                            {errors.country}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.taxId}>
                        <Typography variant="body2">
                          Tax Identification Number (e.g., GSTIN / PAN / VAT) *
                        </Typography>
                        <TextField
                          name="taxId"
                          value={formData.taxId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.taxId}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                          }}
                          helperText={errors.taxId}
                        />
                      </FormControl>
                    </div>
                  </div>
                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-6">
                      <FormControl
                        fullWidth
                        error={!!errors.certificateOfIncorporation}
                      >
                        <Typography variant="body2">
                          Certificate of Incorporation Document
                        </Typography>
                        <TextField
                          name="certificateOfIncorporation"
                          type="file"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.certificateOfIncorporation}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                          }}
                          helperText={errors.certificateOfIncorporation}
                          inputProps={{ accept: ".pdf,.doc,.docx" }}
                        />
                        {formData.certificateOfIncorporation &&
                          renderFilePreview(
                            formData.certificateOfIncorporation,
                            "certificateOfIncorporation"
                          )}
                      </FormControl>
                    </div>
                    <div className="col-md-6">
                      <FormControl fullWidth error={!!errors.taxIdProof}>
                        <Typography variant="body2">
                          Tax Identification Proof (EIN)
                        </Typography>
                        <TextField
                          name="taxIdProof"
                          type="file"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.taxIdProof}
                          disabled={readOnly} // Disable in read-only mode
                          variant="outlined"
                          sx={{
                            backgroundColor: readOnly ? "#f5f5f5" : "white",
                          }}
                          helperText={errors.taxIdProof}
                          inputProps={{ accept: ".pdf,.doc,.docx" }}
                        />
                        {formData.taxIdProof &&
                          renderFilePreview(formData.taxIdProof, "taxIdProof")}
                      </FormControl>
                    </div>
                  </div>

                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-md-12">
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="verifyConfirm"
                            checked={formData.verifyConfirm}
                            onChange={(e) => {
                              if (readOnly) return; // Prevent changes in read-only mode
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                verifyConfirm: checked,
                              }));
                              if (!checked) {
                                setErrors((prev) => ({
                                  ...prev,
                                }));
                              }
                            }}
                            disabled={readOnly} // Disable in read-only mode
                            sx={{
                              color: "orange",
                              "&.Mui-checked": { color: "orange" },
                              "&:hover": {
                                backgroundColor: "rgba(255, 165, 0, 0.1)",
                              },
                            }}
                            helperText={errors.verifyConfirm}
                          />
                        }
                        label="I authorize verification of my identity for compliance
                                   purposes."
                      />
                    </div>
                  </div>
                </div>
              </Box>

              <Box sx={{ mt: 4, textAlign: "right" }}>
                <Button
                  style={{ backgroundColor: "#de6b14", color: "white" }}
                  className="submit-btn-more"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goNext}
                >
                  Next Step
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 1: Ownership */}
          {activeTab === 1 && (
            <Box>
              <SectionHeader variant="h6" gutterBottom>
                Ownership & Management
              </SectionHeader>
              <Box
                sx={{
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                }}
              >
                <FormControl fullWidth>
                  <Typography variant="body2">
                    Promoter / Founder Name
                  </Typography>
                  <TextField
                    name="promoterName"
                    value={formData.promoterName}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">Ownership Disclosed?</Typography>
                  <RadioGroup
                    row
                    name="ownershipDisclosed"
                    value={formData.ownershipDisclosed}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">% Shareholding Known</Typography>
                  <TextField
                    name="shareholding"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.shareholding}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">
                    Management Experience (Years)
                  </Typography>
                  <TextField
                    name="mgmtExperience"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.mgmtExperience}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                  <Typography variant="body2">Key Management Names</Typography>
                  <TextField
                    name="directorNames"
                    value={formData.directorNames}
                    onChange={handleChange}
                   
                    disabled={readOnly}
                    placeholder="List of directors separated by commas"
                  />
                </FormControl>
              </Box>

              <Box
                sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  style={{ color: "#de6b14" }}
                  onClick={goPrev}
                >
                  Back
                </Button>
                <Button
                  style={{ backgroundColor: "#de6b14", color: "white" }}
                  className="submit-btn-more"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goNext}
                >
                  Next Step
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 2: Trade History */}
          {activeTab === 2 && (
            <Box>
              <SectionHeader variant="h6" gutterBottom>
                Trade History & Operations
              </SectionHeader>
              <Box
                sx={{
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                }}
              >
                <FormControl fullWidth>
                  <Typography variant="body2">
                    Years in Commodity Trading
                  </Typography>
                  <TextField
                    name="tradingYears"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.tradingYears}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">
                    Annual Trade Volume Range
                  </Typography>
                  <Select
                    name="tradeVolume"
                    value={formData.tradeVolume}
                    onChange={handleChange}
                    disabled={readOnly}
                  >
                    <MenuItem value="">Select Range</MenuItem>
                    <MenuItem value="low">Low (&lt; $1M)</MenuItem>
                    <MenuItem value="medium">Medium ($1M - $10M)</MenuItem>
                    <MenuItem value="high">High ($10M - $50M)</MenuItem>
                    <MenuItem value="very_high">Very High ($50M+)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">
                    Export/Import Experience?
                  </Typography>
                  <Select
                    name="eximExperience"
                    value={formData.eximExperience}
                    onChange={handleChange}
                    disabled={readOnly}
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>

                <Box />

                <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                  <Typography variant="body2">
                    Main Commodities Traded (comma separated)
                  </Typography>
                  <TextField
                    name="commodities"
                    value={formData.commodities}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. Crude, Soybeans, Iron Ore"
                  />
                </FormControl>

                <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                  <Typography variant="body2">Countries Traded With  (comma separated)</Typography>
                  <TextField
                    name="countriesTraded"
                    value={formData.countriesTraded}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="e.g. China, Brazil, India"
                  />
                </FormControl>
              </Box>

              <Box
                sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  style={{ color: "#de6b14" }}
                  onClick={goPrev}
                >
                  Back
                </Button>
                <Button
                  style={{ backgroundColor: "#de6b14", color: "white" }}
                  className="submit-btn-more"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goNext}
                >
                  Next Step
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 3: Compliance */}
          {activeTab === 3 && (
            <Box>
              <SectionHeader variant="h6" gutterBottom>
                Compliance Declarations
              </SectionHeader>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f0f9ff",
                  borderRadius: 2,
                  borderLeft: "4px solid #0ea5e9",
                  mb: 3,
                }}
              >
                <Typography variant="body2">
                  Please provide declarations based on internal records. False
                  declarations may result in blacklisting.
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gap: 3 }}>
                <FormControl>
                  <Typography variant="body2">AML Policy Declared</Typography>
                  <RadioGroup
                    row
                    name="amlPolicy"
                    value={formData.amlPolicy}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl>
                  <Typography variant="body2">
                    KYC Documents Submitted
                  </Typography>
                  <RadioGroup
                    row
                    name="kycSubmitted"
                    value={formData.kycSubmitted}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="sanctionsCheck"
                      checked={formData.sanctionsCheck}
                      onChange={handleChange}
                      disabled={readOnly}
                    />
                  }
                  label="Sanctions Compliance Declaration"
                />

                <FormControl>
                  <Typography variant="body2">
                    Litigation Disclosure (Ongoing legal cases)
                  </Typography>
                  <RadioGroup
                    row
                    name="litigation"
                    value={formData.litigation}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box
                sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  style={{ color: "#de6b14" }}
                  onClick={goPrev}
                >
                  Back
                </Button>
                <Button
                  style={{ backgroundColor: "#de6b14", color: "white" }}
                  className="submit-btn-more"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goNext}
                >
                  Next Step
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 4: Operational */}
          {activeTab === 4 && (
            <>
              <Box>
                <SectionHeader variant="h6" gutterBottom>
                  Digital & Operational Presence
                </SectionHeader>
                <Box className="tab-content">
                  <div>
                    <div className="row" style={{ marginBottom: "10px" }}>
                      <div className="col-md-12">
                        <FormControl
                          fullWidth
                          error={!!errors.registeredAddress}
                        >
                          <Typography variant="body2">
                            Registered Address *
                          </Typography>
                          <textarea
                            id="registeredAddress"
                            className="form-control"
                            rows="2"
                            name="registeredAddress"
                            value={formData.registeredAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={readOnly} // Disable in read-only mode
                            helperText={errors.registeredAddress}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="row" style={{ marginBottom: "10px" }}>
                      <div className="col-md-12">
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isOperationalDifferent"
                              checked={formData.isOperationalDifferent}
                              onChange={(e) => {
                                if (readOnly) return; // Prevent changes in read-only mode
                                const checked = e.target.checked;
                                setFormData((prev) => ({
                                  ...prev,
                                  isOperationalDifferent: checked,
                                  operationalAddress: checked
                                    ? ""
                                    : prev.registeredAddress,
                                }));
                                if (!checked) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    operationalAddress: undefined,
                                  }));
                                }
                              }}
                              disabled={readOnly} // Disable in read-only mode
                              sx={{
                                color: "orange",
                                "&.Mui-checked": { color: "orange" },
                                "&:hover": {
                                  backgroundColor: "rgba(255, 165, 0, 0.1)",
                                },
                              }}
                            />
                          }
                          label="Operational Address is different from Registered Address"
                        />
                        {formData.isOperationalDifferent && (
                          <FormControl
                            fullWidth
                            error={!!errors.operationalAddress}
                          >
                            <Typography
                              variant="body2"
                              style={{ marginTop: "8px" }}
                            >
                              Operational Address
                            </Typography>
                            <textarea
                              id="operationalAddress"
                              className="form-control"
                              rows="2"
                              name="operationalAddress"
                              value={formData.operationalAddress}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={readOnly} // Disable in read-only mode
                              error={!!errors.operationalAddress}
                              helperText={errors.operationalAddress}
                            />
                          </FormControl>
                        )}
                        {!formData.isOperationalDifferent && (
                          <FormControl
                            fullWidth
                            error={!!errors.operationalAddress}
                          >
                            <Typography
                              variant="body2"
                              style={{ marginTop: "8px" }}
                            >
                              Operational Address
                            </Typography>
                            <textarea
                              id="operationalAddress"
                              className="form-control"
                              rows="2"
                              name="operationalAddress"
                              value={formData.operationalAddress}
                              disabled
                            />
                          </FormControl>
                        )}
                      </div>
                    </div>
                    <div className="row" style={{ marginBottom: "10px" }}>
                      <div className="col-md-6">
                        <FormControl fullWidth error={!!errors.websiteUrl}>
                          <Typography variant="body2">
                          Company Website URL
                          </Typography>
                          <TextField
                            type="url"
                            name="websiteUrl"
                            placeholder="http://"
                            value={formData.websiteUrl}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.websiteUrl}
                            disabled={readOnly} // Disable in read-only mode
                            variant="outlined"
                            sx={{
                              backgroundColor: readOnly ? "#f5f5f5" : "white",
                            }}
                            helperText={errors.websiteUrl}
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl fullWidth error={!!errors.websiteEmail}>
                          <Typography variant="body2">
                          Official Email Domain *
                          </Typography>
                          <TextField
                            type="email"
                            name="websiteEmail"
                            value={formData.websiteEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.websiteEmail}
                            disabled={readOnly} // Disable in read-only mode
                            variant="outlined"
                            sx={{
                              backgroundColor: readOnly ? "#f5f5f5" : "white",
                            }}
                            helperText={errors.websiteEmail}
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl fullWidth error={!!errors.websiteAge}>
                          <Typography variant="body2">
                            Years Website Active
                          </Typography>
                          <TextField
                            name="websiteAge"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={formData.websiteAge}
                            onChange={handleChange}
                            disabled={readOnly}
                            error={!!errors.websiteAge}
                            variant="outlined"
                            sx={{
                              backgroundColor: readOnly ? "#f5f5f5" : "white",
                            }}
                            helperText={errors.websiteAge}
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl fullWidth error={!!errors.contactNumber}>
                          <Typography variant="body2">
                            Business Phone Number *
                          </Typography>
                          <TextField
                            type="tel"
                            name="contactNumber"
                            value={formData.contactNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.contactNumber}
                            disabled={readOnly} // Disable in read-only mode
                            variant="outlined"
                            sx={{
                              backgroundColor: readOnly ? "#f5f5f5" : "white",
                            }}
                            helperText={errors.contactNumber}
                          />
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIcon />}
                    style={{ color: "#de6b14" }}
                    onClick={goPrev}
                  >
                    Back
                  </Button>
                  <Button
                    style={{ backgroundColor: "#de6b14", color: "white" }}
                    className="submit-btn-more"
                    endIcon={<ArrowForwardIcon />}
                    onClick={goNext}
                  >
                    Next Step
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {/* Tab 5: Banking */}
          {activeTab === 5 && (
            <Box>
              <SectionHeader variant="h6" gutterBottom>
                Banking Relationship
              </SectionHeader>
              <Box
                sx={{
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                }}
              >
                <FormControl fullWidth>
                  <Typography variant="body2">
                    Annual Turnover (Last Reported Year)
                  </Typography>
                  <TextField
                    type="number"
                    name="annualTurnover"
                    value={formData.annualTurnover}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly} // Disable in read-only mode
                    variant="outlined"
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    helperText={errors.annualTurnover}
                    inputProps={{ min: 0 }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">Paid-up Capital</Typography>
                  <TextField
                    type="number"
                    name="paidUpCapital"
                    value={formData.paidUpCapital}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly} // Disable in read-only mode
                    variant="outlined"
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    helperText={errors.paidUpCapital}
                    inputProps={{ min: 0 }}
                  />
                </FormControl>

                <FormControl fullWidth error={!!errors.bankName}>
                  <Typography variant="body2">
                    Primary Operating Bank Name *
                  </Typography>
                  <TextField
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.bankName}
                    disabled={readOnly} // Disable in read-only mode
                    variant="outlined"
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    helperText={errors.bankName}
                  />
                </FormControl>

                <FormControl fullWidth error={!!errors.accountNumber}>
                  <Typography variant="body2">Bank Account Number *</Typography>
                  <TextField
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.accountNumber}
                    disabled={readOnly} // Disable in read-only mode
                    variant="outlined"
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    helperText={errors.accountNumber}
                  />
                </FormControl>

                <FormControl fullWidth error={!!errors.ifscSwift}>
                  <Typography variant="body2">IFSC / SWIFT Code *</Typography>
                  <TextField
                    name="ifscSwift"
                    value={formData.ifscSwift}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.ifscSwift}
                    disabled={readOnly} // Disable in read-only mode
                    variant="outlined"
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    helperText={errors.ifscSwift}
                  />
                </FormControl>

                <FormControl fullWidth error={!!errors.sourceOfFunds}
                    sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}>
                  <Typography variant="body2">
                    Primary Source of Funds
                  </Typography>
                  <Select
                    name="sourceOfFunds"
                    value={formData.sourceOfFunds}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.sourceOfFunds}
                    disabled={readOnly} // Disable in read-only mode
                  >
                    <MenuItem value="" disabled>
                      Select Source
                    </MenuItem>
                   
                    {sourcesOfFundsMaster.map((p) => (
                      <MenuItem key={p.ID} value={p.Value}>
                        {p.Value}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.sourceOfFunds && (
                    <Typography variant="caption" color="error">
                      {errors.sourceOfFunds}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">Account Type</Typography>
                  <Select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleChange}
                    disabled={readOnly}
                  >
                    <MenuItem value="" disabled>
                      Select Account Type
                    </MenuItem>
                    <MenuItem value="current">Current / Checking</MenuItem>
                    <MenuItem value="savings">Savings</MenuItem>
                    <MenuItem value="overdraft">
                      Overdraft / Credit Line
                    </MenuItem>
                  </Select>
                  {errors.accountType && (
                    <Typography variant="caption" color="error">
                      {errors.accountType}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">Years with Bank</Typography>
                  <TextField
                    name="bankYears"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.bankYears}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2">
                    LC / BG Usage History?
                  </Typography>
                  <RadioGroup
                    row
                    name="lcUsage"
                    value={formData.lcUsage}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </FormControl>

                <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                  <Typography variant="body2">
                    Last Audited Financials
                  </Typography>
                  <TextField
                    name="lastAuditedFinancials"
                    type="file"
                    onChange={handleChange}
                    disabled={readOnly}
                    InputProps={{ accept: ".pdf,.doc,.docx" }}
                  />
                  {formData.lastAuditedFinancials &&
                    renderFilePreview(
                      formData.lastAuditedFinancials,
                      "lastAuditedFinancials"
                    )}
                </FormControl>
              </Box>

              <Box
                sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  style={{ color: "#de6b14" }}
                  onClick={goPrev}
                >
                  Back
                </Button>
              </Box>
            </Box>
          )}

          {/* Tab 6: Remarks */}
          {activeTab === 6 && (
            <Box>
              {isUpdateMode ? (
                <RemarksThread
                  kybId={userKybID}
                  currentUserId={activeUser}
                  currentUserName={activeUserName}
                  status={formData.Status}
                />
              ) : (
                <Typography
                  sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}
                >
                  Remarks will be available after submission.
                </Typography>
              )}
              <Box sx={{ mt: 4 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  style={{ color: "#de6b14" }}
                  onClick={goPrev}
                >
                  Back
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
};

export default KYBVerification;
